import React, { useMemo, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import Delete from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { v4 as uuidv4 } from "uuid";
import {
  useMaterialReactTable,
  MaterialReactTable,
} from "material-react-table";
import { useTranslation } from "react-i18next";
import { MRT_Localization_ES } from "material-react-table/locales/es";
import { MRT_Localization_EN } from "material-react-table/locales/en";
import { EditOriginalNameCell } from "./TableMapper/EditOriginalNameCell";
import { EditMappedNameCell } from "./TableMapper/EditMappedNameCell";

const TableMapper = ({
  editing,
  editingResponse,
  setEditingResponse,
  edit,
  route,
  handleEditInput,
  locatedError,
  setLocatedError,
  tableRef,
}) => {

  const {t, i18n} = useTranslation("tableMapper");
  const editingValuesRef = useRef({});
  const setEditingValue = (rowId, key, value) => {
    if (!editingValuesRef.current[rowId]) {
      editingValuesRef.current[rowId] = {};
    }
    editingValuesRef.current[rowId][key] = value;
  };

  const editOriginalNameCell = useCallback(
    (props) => <EditOriginalNameCell 
      {...props} 
      locatedError={locatedError} 
      setEditingValue={setEditingValue}
    />,
    [locatedError]
  );
  
  const editMappedNameCell = useCallback(
    (props) => <EditMappedNameCell 
      {...props} 
      locatedError={locatedError} 
      setEditingValue={setEditingValue}
    />,
    [locatedError]
  );

  const columns = useMemo(
    () => [
      {
        accessorKey: "originalName",
        header: t("originalNameHeader"),
        required: true,
        Edit: editOriginalNameCell,
      },
      {
        accessorKey: "mappedName",
        header: t("mappedNameHeader"),
        required: true,
        Edit: editMappedNameCell,
      },
    ],
    [t, editOriginalNameCell, editMappedNameCell, editing, edit, route, locatedError, i18n.language]
  );
  
  const data = (editing ? edit?.responseMapping : route?.responseMapping) || [];

  const table = useMaterialReactTable({
    columns,
    data,
    getRowId: (row) => row.tempId || row._id || row.id,
    enableColumnHeaders: false,
    enableColumnActions: false,
    enableColumnFilters: false,
    enableGlobalFilter: false,
    enablePagination: false,
    enableFilters: false,
    enableSorting: false,
    enableHiding: false,
    enableFullScreenToggle: false,
    enableDensityToggle: false,
    createDisplayMode: "row",
    editDisplayMode: "row",
    enableRowActions: true,
    enableEditing: true,
    enableDelete: true,
    positionActionsColumn: "last",
    renderBottomToolbar: false,
    localization: i18n.language === "ES" ? MRT_Localization_ES : MRT_Localization_EN,

    renderRowActions: ({ row, table }) => (
      <Box sx={{ display: "flex", gap: 1 }}>
        <Tooltip title={t("editTooltip")}>
          <IconButton
            id="buttonEditMapper"
            disabled={!editing}
            onClick={() => {
              if (table.getState().editingRow) {
                table.setEditingRow(null, true);
              }
              setEditingResponse(true);
              table.setEditingRow(row);
            }}>
            <EditIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title={t("deleteTooltip")}>
          <IconButton
            id="buttonDeleteMapper"
            color="error"
            disabled={!editing}
            onClick={() => handleEditInput(row.original, "routeMapperDelete")}>
            <Delete />
          </IconButton>
        </Tooltip>
      </Box>
    ),
    renderTopToolbarCustomActions: ({ table }) => (
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%",
        }}
      >
        <Typography variant="h5">
          {t("responseMappingTitle")}
        </Typography>
    
        <Tooltip title={t("addNewResponseTooltip")}>
          <IconButton
            id="buttonAddMapper"
            sx={{
              backgroundColor: "primary.main",
            }}
            disabled={!editing || editingResponse}
            onClick={() => {
              setEditingResponse(true);
              table.setCreatingRow(true);
            }}
          >
            <AddIcon />
          </IconButton>
        </Tooltip>
      </Box>
    ),
    onCreatingRowSave: ({ row }) => {
      const rowId = row.id;
      const values = editingValuesRef.current[rowId]; 
      const result = handleEditInput(values, "routeMapperAdd", rowId);
    
      if (result) {
        const tempId = uuidv4();
        const newRow = { ...values, tempId };
        editingValuesRef.current[tempId] = newRow;
        delete editingValuesRef.current[rowId];
        table.setCreatingRow(false);
      }
    },
    onEditingRowSave: ({ row }) => {
      const rowId = row.id;
      const values = editingValuesRef.current[rowId];
      handleEditInput({ values, row }, "routeMapperEdit");
      delete editingValuesRef.current[rowId];
    },
    onCreatingRowCancel: () => {
      setEditingResponse(false);
      editingValuesRef.current = {};
      setLocatedError({ rowId: "", fields: {} });
    },
    
    onEditingRowCancel: () => {
      setEditingResponse(false);
      editingValuesRef.current = {};
      setLocatedError({ rowId: "", fields: {} });
    },
  });

  tableRef.current = table;

  return (
    <Box sx={{ width: "100%" }}>
      <MaterialReactTable table={table}/>
    </Box>
  );
};

TableMapper.propTypes = {
  editing: PropTypes.bool,
  editingResponse: PropTypes.bool,
  setEditingResponse: PropTypes.func,
  edit: PropTypes.shape({
    responseMapping: PropTypes.arrayOf(PropTypes.object),
  }),
  route: PropTypes.shape({
    responseMapping: PropTypes.arrayOf(PropTypes.object).isRequired,
  }).isRequired,
  handleEditInput: PropTypes.func,
  locatedError: PropTypes.shape({
    rowId: PropTypes.string,
    fields: PropTypes.object,
  }),
  setLocatedError: PropTypes.func,
  tableRef: PropTypes.shape({
    current: PropTypes.any,
  }),
};

export default TableMapper;